/** UXP-00012 — Platform Administration framework (mirrors pkg/platformadmin) */

export const UXP_PA_ID = 'UXP-00012';

export interface CommandCenterKPI {
  label: string;
  value: string;
  change: string;
  positive: boolean;
}

export interface OrganizationRow {
  name: string;
  users: number;
  workspaces: number;
  status: string;
  plan: string;
}

export interface PermissionRow {
  scope: string;
  granted: number;
  total: number;
}

export interface AIProviderRow {
  provider: string;
  status: string;
  cost: string;
  latency: string;
}

export interface PromptRow {
  scope: string;
  version: string;
  status: string;
  tests: number;
}

export interface IntegrationRow {
  provider: string;
  status: string;
  lastSync: string;
}

export interface SecurityRow {
  label: string;
  status: string;
  score: number;
}

export interface PlatformOpsRow {
  metric: string;
  value: string;
  health: string;
}

export const COMMAND_CENTER_METRICS = [
  'Platform Health', 'Tenant Health', 'Usage', 'Revenue', 'AI Usage', 'Credits',
  'Marketplace Status', 'Worker Health', 'Security', 'Compliance',
];

export const ORG_MANAGEMENT_FEATURES = [
  'Organizations', 'Business Units', 'Departments', 'Workspaces', 'Brands',
  'Marketplace Groups', 'Lifecycle', 'Activation', 'Suspension', 'Deletion',
];

export const USER_ROLE_FEATURES = [
  'Users', 'Groups', 'Departments', 'Teams', 'Invitations',
  'Role Templates', 'Permission Inheritance', 'Delegated Access',
];

export const PERMISSION_SCOPES = [
  'Modules', 'Screens', 'Fields', 'Buttons', 'AI Features',
  'Marketplace Access', 'Brand Access', 'API Access', 'Environment Access',
];

export const AI_GOVERNANCE_FEATURES = [
  'LLM Providers', 'Routing', 'Fallback', 'Confidence Thresholds',
  'Quality Rules', 'Cost Controls', 'Versioning', 'Safety Rules',
];

export const PROMPT_GOVERNANCE_FEATURES = [
  'Global Rules', 'Organization Rules', 'Brand Rules', 'Marketplace Rules',
  'Product Rules', 'Prompt Templates', 'Versioning', 'Testing', 'Rollback', 'Analytics',
];

export const DNA_TYPES = [
  'Brand DNA', 'Product DNA', 'Customer Intent DNA', 'Growth DNA',
  'Supply Chain DNA', 'CX DNA', 'Financial DNA',
];

export const AUTOMATION_FEATURES = [
  'Business Rules', 'AI Agents', 'Workflow Automation', 'Scheduled Jobs',
  'Marketplace Events', 'Retry Policies', 'Notifications', 'Escalations',
];

export const FEATURE_MANAGEMENT = [
  'Feature Flags', 'Beta Features', 'Organization Features',
  'Role-based Features', 'Marketplace Features', 'A/B Tests',
];

export const INTEGRATION_PROVIDERS = [
  'Amazon', 'Google', 'Microsoft', 'Slack', 'Teams', 'Tally',
  'Shopify', 'Shiprocket', 'Webhooks', 'REST APIs',
];

export const API_MANAGEMENT_FEATURES = [
  'API Keys', 'OAuth', 'Rate Limits', 'Usage Monitoring', 'Logs', 'SDK Management', 'Developer Access',
];

export const SECURITY_FEATURES = [
  'MFA', 'SSO', 'Session Management', 'Device Trust', 'Encryption',
  'Secrets Management', 'Password Policies', 'Audit',
];

export const COMPLIANCE_STANDARDS = [
  'GDPR', 'DPDP', 'SOC2 Readiness', 'ISO27001 Readiness',
  'Retention', 'Consent', 'Legal Hold', 'Audit Export',
];

export const PLATFORM_OPS_METRICS = [
  'Workers', 'Queues', 'Database', 'Redis', 'Storage',
  'Background Jobs', 'Marketplace APIs', 'LLM Providers', 'Latency',
];

export const BILLING_FEATURES = [
  'Credits', 'Subscriptions', 'Invoices', 'Usage', 'AI Costs',
  'Marketplace Costs', 'Recharge', 'Analytics',
];

export const AUDIT_EVENTS = [
  'Authentication', 'Authorization', 'AI Decisions', 'Publishing',
  'Approvals', 'Role Changes', 'Platform Changes', 'Integration Changes',
];

export const ANALYTICS_METRICS = [
  'Tenant Growth', 'MRR', 'ARR', 'Churn', 'Feature Usage',
  'AI Adoption', 'Platform Performance', 'Credit Consumption',
];

export const ACCESSIBILITY_STANDARDS = [
  'WCAG 2.2 AA', 'Keyboard Navigation', 'Screen Reader Support', 'Reduced Motion', 'Responsive Design',
];

export const ARCHITECTURE_STAGES = [
  'Enterprise Command Center', 'Organization Management', 'User & Role Management', 'Permission Engine',
  'AI Governance', 'Prompt Governance', 'DNA Governance', 'Automation Engine',
  'Feature Management', 'Integration Center', 'Security & Compliance', 'Platform Operations', 'Credit & Billing',
];

export const LIVE_ROUTES: Record<string, string> = {
  admin: '/admin',
  governance: '/governance',
  billing: '/billing',
  integrations: '/integrations',
  sre: '/sre',
};

export const DEMO_KPIS: CommandCenterKPI[] = [
  { label: 'Platform Health', value: '99.2%', change: 'stable', positive: true },
  { label: 'Tenant Health', value: '97%', change: '+1pp', positive: true },
  { label: 'Active Usage', value: '842 tenants', change: '+8%', positive: true },
  { label: 'MRR', value: '₹42.8L', change: '+14%', positive: true },
  { label: 'AI Usage', value: '2.4M tokens/day', change: '+18%', positive: true },
  { label: 'Credits', value: '₹8.2L remaining', change: '-12%', positive: false },
  { label: 'Marketplace APIs', value: 'All healthy', change: '100%', positive: true },
  { label: 'Worker Health', value: '48/48', change: 'all running', positive: true },
  { label: 'Security Score', value: '94/100', change: '+2', positive: true },
  { label: 'Compliance', value: 'SOC2 ready', change: 'on track', positive: true },
];

export const DEMO_ORGS: OrganizationRow[] = [
  { name: 'SoundWave Electronics', users: 24, workspaces: 3, status: 'active', plan: 'Enterprise' },
  { name: 'Mumbai Wellness Co.', users: 8, workspaces: 1, status: 'active', plan: 'Growth' },
  { name: 'Guangzhou IoT Ltd.', users: 12, workspaces: 2, status: 'trial', plan: 'Starter' },
];

export const DEMO_PERMISSIONS: PermissionRow[] = [
  { scope: 'Modules', granted: 42, total: 48 },
  { scope: 'AI Features', granted: 18, total: 24 },
  { scope: 'Marketplace Access', granted: 6, total: 8 },
  { scope: 'API Access', granted: 12, total: 16 },
];

export const DEMO_AI: AIProviderRow[] = [
  { provider: 'OpenAI GPT-4o', status: 'primary', cost: '₹1.2L/mo', latency: '840ms' },
  { provider: 'Anthropic Claude', status: 'fallback', cost: '₹42K/mo', latency: '920ms' },
  { provider: 'Google Gemini', status: 'routing', cost: '₹28K/mo', latency: '680ms' },
];

export const DEMO_PROMPTS: PromptRow[] = [
  { scope: 'Global Rules', version: 'v2.4.1', status: 'published', tests: 847 },
  { scope: 'Brand DNA Prompts', version: 'v1.8.2', status: 'published', tests: 234 },
  { scope: 'Listing Optimizer', version: 'v3.1.0', status: 'testing', tests: 56 },
];

export const DEMO_INTEGRATIONS: IntegrationRow[] = [
  { provider: 'Amazon', status: 'connected', lastSync: '2 min ago' },
  { provider: 'Tally', status: 'connected', lastSync: '6h ago' },
  { provider: 'Slack', status: 'connected', lastSync: 'live' },
  { provider: 'Shiprocket', status: 'pending', lastSync: '—' },
];

export const DEMO_SECURITY: SecurityRow[] = [
  { label: 'MFA Enforcement', status: 'enabled', score: 100 },
  { label: 'GDPR', status: 'compliant', score: 96 },
  { label: 'DPDP', status: 'compliant', score: 94 },
  { label: 'SOC2 Readiness', status: 'in progress', score: 88 },
];

export const DEMO_OPS: PlatformOpsRow[] = [
  { metric: 'Workers', value: '48 running', health: 'healthy' },
  { metric: 'Queues', value: '12 queues · 2.4K jobs', health: 'healthy' },
  { metric: 'Database', value: '142ms p99', health: 'healthy' },
  { metric: 'Redis', value: '98% hit rate', health: 'healthy' },
  { metric: 'LLM Providers', value: '3 active', health: 'healthy' },
  { metric: 'Latency', value: '420ms API p95', health: 'healthy' },
];

export function buildPlatformAdminDashboard() {
  return {
    command_center_metrics: COMMAND_CENTER_METRICS.length,
    org_features: ORG_MANAGEMENT_FEATURES.length,
    permission_scopes: PERMISSION_SCOPES.length,
    ai_governance_features: AI_GOVERNANCE_FEATURES.length,
    integration_providers: INTEGRATION_PROVIDERS.length,
  };
}
